import { useEffect, useMemo, useRef, useState } from "react";
import { audio } from "../fx/audio";
import { eraOf } from "../fx/logic";
import { PACKS } from "../fx/score";
import { FX_LEVELS, useFx } from "../fx/state";
import { loadSearch } from "../lib/data";
import { prepare, search, type Hit, type Prepared } from "../lib/search";
import { useApp } from "../lib/store";
import { yearLabel } from "../lib/time";
import { SoundButton } from "./Replays";

export function TopBar() {
  const setAbout = useApp((s) => s.setAbout);
  const layers = useApp((s) => s.layers);
  const toggleLayer = useApp((s) => s.toggleLayer);
  const panel = useApp((s) => s.panel);
  const [menu, setMenu] = useState(false);
  const fxLevel = useFx((s) => s.level);
  const setFxLevel = useFx((s) => s.setLevel);
  const sound = useFx((s) => s.sound);
  const setSound = useFx((s) => s.setSound);
  const volume = useFx((s) => s.volume);
  const setVolume = useFx((s) => s.setVolume);
  const era = useApp((s) => eraOf(s.t));
  const openReplays = () => useApp.getState().setPanel(panel === "replays" ? "moment" : "replays");
  return (
    <header className="topbar">
      <a className="brand" href="/" aria-label="Conflicts Atlas home">
        <span className="brand-mark" aria-hidden>◉</span>
        <span className="brand-name">Conflicts Atlas</span>
        <span className="brand-sub">war &amp; peace through time</span>
      </a>
      <SearchBox />
      <div className="topbar-actions">
        <button className="btn btn-replays" aria-pressed={panel === "replays"} onClick={openReplays} data-testid="open-replays">
          <span aria-hidden>▶</span> Replays
        </button>
        <SoundButton className="btn icon topbar-sound" />
        <div className="menu-wrap">
          <button className="btn" aria-expanded={menu} onClick={() => setMenu(!menu)}>Layers</button>
          {menu && (
            <div className="menu" role="menu" onMouseLeave={() => setMenu(false)}>
              <div className="menu-head">On the map</div>
              {([["areas", "Conflict areas"], ["events", "Battles & events"], ["labels", "Conflict names"], ["borders", "Historical borders"]] as const).map(([k, label]) => (
                <label key={k} className="menu-item">
                  <input type="checkbox" checked={layers[k]} onChange={() => toggleLayer(k)} /> {label}
                </label>
              ))}
              <div className="menu-head">Effects</div>
              {FX_LEVELS.map((l) => (
                <label key={l.key} className="menu-item menu-radio" title={l.note}>
                  <input type="radio" name="fx-level" checked={fxLevel === l.key} onChange={() => setFxLevel(l.key)} />
                  <span><span>{l.label}</span><span className="menu-note">{l.note}</span></span>
                </label>
              ))}
              <label className="menu-item">
                <input type="checkbox" checked={sound && fxLevel !== "off"} disabled={fxLevel === "off"} onChange={() => setSound(!sound)} /> Sound
              </label>
              <label className="menu-item menu-volume">
                <span>Volume</span>
                <input type="range" min={0} max={1} step={0.05} value={volume} disabled={!sound || fxLevel === "off"}
                  onChange={(e) => setVolume(Number(e.target.value))} aria-label="Volume" />
              </label>
              <div className="menu-head">Hear each period</div>
              {PACKS.map((p) => (
                <button key={p.era} className={`menu-item menu-hear${p.era === era ? " now" : ""}`} disabled={!sound || fxLevel === "off"}
                  title={`What you hear: ${p.heard}`} aria-label={`Hear a battle: ${p.name}, ${p.years}`}
                  onClick={() => { audio.setEnabled(true, volume); audio.preview(p.hear); }}>
                  <span className="menu-play" aria-hidden>▶</span>
                  <span><span>{p.name}</span><span className="menu-note">{p.years}{p.era === era ? " · the period on the map now" : ""}</span></span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button className="btn" onClick={() => setAbout(true)}>About</button>
      </div>
    </header>
  );
}

function SearchBox() {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState<Prepared | null>(null);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const c = useApp((s) => s.conflicts);
  const select = useApp((s) => s.select);
  const setT = useApp((s) => s.setT);
  const setSpan = useApp((s) => s.setSpan);
  const fly = useApp((s) => s.fly);
  const hits = useMemo(() => (idx && q.trim().length >= 2 ? search(idx, q, 12) : []), [idx, q]);
  const pendingEnter = useRef(false);

  const ensure = () => { if (!idx) loadSearch().then((s) => setIdx(prepare(s))).catch(console.error); };
  // warm the index in idle time so the first search is instant
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const id = w.requestIdleCallback ? w.requestIdleCallback(ensure) : window.setTimeout(ensure, 4000);
    return () => { if (!w.requestIdleCallback) window.clearTimeout(id); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Enter pressed while the index was still loading: take the best match as soon as it is there
  useEffect(() => {
    if (pendingEnter.current && hits.length) { pendingEnter.current = false; choose(hits[0]); }
  }, [hits]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); input.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const choose = (h: Hit) => {
    setOpen(false);
    setQ("");
    input.current?.blur();
    if (h.kind === "c" && c) {
      select(h.ref as number, { jump: true, fly: true });
    } else {
      setT(h.t + 1 / 730);
      setSpan(Math.min(useApp.getState().span, 12));
      // events: find coordinates through their chunk on the map (the detail of the event's conflict lists it)
      fetchEventAndFly(String(h.ref), h.t, fly);
    }
  };
  return (
    <div className="search" role="search">
      <input
        ref={input}
        type="search"
        placeholder="Search wars and battles…  ( / )"
        aria-label="Search wars and battles"
        value={q}
        onFocus={() => { ensure(); setOpen(true); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setCursor(0); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { setCursor(Math.min(cursor + 1, hits.length - 1)); e.preventDefault(); }
          else if (e.key === "ArrowUp") { setCursor(Math.max(cursor - 1, 0)); e.preventDefault(); }
          else if (e.key === "Enter") { if (hits[cursor]) choose(hits[cursor]); else if (!idx) { pendingEnter.current = true; ensure(); } }
          else if (e.key === "Escape") { setOpen(false); input.current?.blur(); }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && q.trim().length >= 2 && (
        <ul className="search-results" role="listbox" data-testid="search-results">
          {!idx && <li className="sr-empty">Loading index…</li>}
          {idx && hits.length === 0 && <li className="sr-empty">No matches</li>}
          {hits.map((h, k) => (
            <li key={`${h.kind}${h.ref}`} role="option" aria-selected={k === cursor} className={k === cursor ? "active" : ""}
              onMouseDown={(e) => { e.preventDefault(); choose(h); }} onMouseEnter={() => setCursor(k)}>
              <span className="sr-name">{h.name}</span>
              <span className="sr-meta">{h.kind === "c" ? "conflict" : "battle / event"} · {yearLabel(Math.floor(h.t))}{h.alias ? ` · “${h.alias}”` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Find a named event's coordinates in its era chunk and fly there. */
async function fetchEventAndFly(id: string, t: number, fly: (x: { lon: number; lat: number; zoom: number }) => void) {
  const st = useApp.getState();
  const chunk = st.meta?.chunks.named.find((c) => t >= c.t0 && t < c.t1);
  if (!chunk) return;
  const { loadNamed } = await import("../lib/data");
  const ev = await loadNamed(chunk);
  const k = ev.id.indexOf(id);
  if (k < 0) return;
  fly({ lon: ev.lon[k], lat: ev.lat[k], zoom: 5.5 });
  const { tToYmd, formatDate } = await import("../lib/time");
  const [y, m, d] = tToYmd(ev.t0[k]);
  st.setEvent({ src: "named", name: ev.name[k], date: formatDate({ y, m, d, prec: ev.pr[k], julian: !!ev.jl[k] }), deaths: ev.d[k] || null, kind: ev.k[k], conflict: ev.c[k], lon: ev.lon[k], lat: ev.lat[k], id, url: ev.u?.[k] || undefined });
}
